import {
  createSimulationModel,
  simulationValues,
  applySimulationHistoryProfile
} from './simulation_presets';
import defaults from './simulation_defaults.json';
import { Ran } from './utils.js';
import {
  sampleGraphValues,
  selectCharacteristicIndices,
  suppressFactorSpikes
} from './info_graph_utils.js';

export const SIMULATION_API_VERSION = defaults.system.api_version;

export const SIMULATION_48_HOUR_HISTORY_POINTS_PER_BLOCK = 360;
export const SIMULATION_FULL_HISTORY_POINTS_PER_BLOCK = 1920;

export function CreateSimulationInit(socket, display_name, type, username) {
  var req = { command: 'mock_initialize', cookie: document.cookie, data: {} };
  req.socket = socket;
  req.data['display_name'] = display_name;
  req.data['type'] = type;
  req.data['mac_address'] = Ran(0, 9) + '-' + Ran(0, 9) + '-' + Ran(0, 9) + '-' + Ran(9, 0);
  req.data['project_id'] = type === 5155 ? 0 : 0;
  req.data['version'] = SIMULATION_API_VERSION;
  req['internal_id'] = display_name.toLowerCase().replace(' ', '_');
  return req;
}

export function CreateSimulationInfoGraphs(model = null, zoneId = '0') {
  const sourceSampleCount = SIMULATION_48_HOUR_HISTORY_POINTS_PER_BLOCK * 2;
  const sampleIntervalSeconds = 240;
  const humidity = [];
  const temperature = [];
  const rootVwc = Array.from({ length: 4 }, () => []);
  const rootEc = Array.from({ length: 4 }, () => []);
  let vwc = 580;

  for (let index = 0; index < sourceSampleCount; index += 1) {
    const sampleOfDay = index % SIMULATION_48_HOUR_HISTORY_POINTS_PER_BLOCK;
    const hour = sampleOfDay / 15;
    const lightsOn = hour >= 6 && hour < 18;
    const lightsOnProgress = Math.min(1, Math.max(0, (hour - 6) / 1.25));
    const lightsOffProgress = Math.min(1, Math.max(0, (hour - 18) / 1.25));
    const dayTemperature = lightsOn
      ? 242 + lightsOnProgress * 38
      : hour >= 18
        ? 280 - lightsOffProgress * 38
        : 242;
    const humidityBase = lightsOn
      ? 640 - lightsOnProgress * 45
      : hour >= 18
        ? 595 + lightsOffProgress * 45
        : 640;
    const gentleNoise = Math.sin(index * 0.73) * 1.8 + Math.sin(index * 0.19) * 1.2;
    temperature.push(Math.round(dayTemperature + gentleNoise));
    humidity.push(Math.round(humidityBase - gentleNoise * 1.5));

    const morningShot = sampleOfDay >= 90 && sampleOfDay < 135 && (sampleOfDay - 90) % 8 === 0;
    const maintenanceShot =
      sampleOfDay >= 135 && sampleOfDay < 240 && (sampleOfDay - 135) % 15 === 0;
    if (morningShot) vwc += 12;
    else if (maintenanceShot) vwc += 7;
    else if (sampleOfDay < 90 || sampleOfDay >= 240) vwc -= 0.28;
    else if (sampleOfDay < 135) vwc -= 0.55;
    else vwc -= 0.32;
    vwc = Math.max(500, Math.min(600, vwc));

    for (let rootZone = 0; rootZone < rootVwc.length; rootZone += 1) {
      const zoneVwc = Math.max(
        500,
        Math.min(600, Math.round(vwc - rootZone * 7 + Math.sin(index / 11 + rootZone) * 2))
      );
      rootVwc[rootZone].push(zoneVwc);
      rootEc[rootZone].push(
        Math.round(205 + (575 - zoneVwc) * 0.75 + Math.cos(index / 17 + rootZone) * 3)
      );
    }
  }

  if (model) {
    const end = Math.floor(Date.now() / 240000) * 240000;
    for (let index = 0; index < sourceSampleCount; index += 1) {
      const timestamp = end - (sourceSampleCount - index) * 240000;
      const sample = applySimulationHistoryProfile(
        createSimulationHistorySample(timestamp),
        timestamp,
        model,
        zoneId
      );
      humidity[index] = sample.humidity;
      temperature[index] = sample.temp;
      for (let root = 0; root < 4; root += 1) {
        const suffix = root === 0 ? '' : `_${root + 1}`;
        rootVwc[root][index] = sample[`vol_water_content${suffix}`];
        rootEc[root][index] = sample[`ec_root_zone${suffix}`];
      }
    }
  }
  const filteredHumidity = suppressFactorSpikes(humidity);
  const filteredTemperature = suppressFactorSpikes(temperature);
  const filteredRootVwc = rootVwc.map((values) => suppressFactorSpikes(values));
  const filteredRootEc = rootEc.map((values) => suppressFactorSpikes(values));
  const climateSampleIndices = selectCharacteristicIndices(
    [filteredHumidity, filteredTemperature],
    48
  );
  const rootZoneSampleIndices = selectCharacteristicIndices(
    filteredRootVwc.flatMap((values, index) => [values, filteredRootEc[index]]),
    64
  );
  return {
    sample_interval_seconds: sampleIntervalSeconds,
    source_sample_count: sourceSampleCount,
    climate_sample_indices: climateSampleIndices,
    humidity_graph: sampleGraphValues(filteredHumidity, climateSampleIndices),
    temp_graph: sampleGraphValues(filteredTemperature, climateSampleIndices),
    root_zone_sample_indices: rootZoneSampleIndices,
    vol_water_content_graphs: filteredRootVwc.map((values) =>
      sampleGraphValues(values, rootZoneSampleIndices)
    ),
    ec_root_zone_graphs: filteredRootEc.map((values) =>
      sampleGraphValues(values, rootZoneSampleIndices)
    )
  };
}

// Keep the public protocol helper; state belongs to the simulation instance.
export function SimulationGetSendValueRequest(
  full,
  internalId,
  zones = [],
  hvac = null,
  model = null
) {
  const state = model || createSimulationModel(zones.length ? 5155 : 5055, zones);
  if (hvac?.data) state.global.hvac = JSON.parse(JSON.stringify(hvac.data));
  return simulationValues(state, full, internalId, zones);
}

export function GenerateSimulationHistory(length, res, block, max, context = null) {
  const resolutionMilliseconds = res * 1000;
  var data = {
    time: [],
    humidity: [],
    temp: [],
    co2: [],
    vpd: [],
    temp_leaf_current: [],
    vol_water_content: [],
    ec_root_zone: [],
    vol_water_content_2: [],
    ec_root_zone_2: [],
    vol_water_content_3: [],
    ec_root_zone_3: [],
    vol_water_content_4: [],
    ec_root_zone_4: []
  };

  // Align every independently requested block to the same resolution boundary.
  const endTime = Math.floor(Date.now() / resolutionMilliseconds) * resolutionMilliseconds;
  let timestamp = endTime - length * resolutionMilliseconds * (max - block);

  for (let index = 0; index < length; index += 1) {
    const sample = applySimulationHistoryProfile(
      createSimulationHistorySample(timestamp),
      timestamp,
      context?.model,
      context?.zoneId
    );
    Object.entries(sample).forEach(([key, value]) => data[key].push(value));
    data.time.push(timestamp);
    timestamp += resolutionMilliseconds;
  }
  return data;
}

const DAY_MILLISECONDS = 24 * 60 * 60 * 1000;

function clamp(value, minimum, maximum) {
  return Math.max(minimum, Math.min(maximum, value));
}

function smoothStep(edgeStart, edgeEnd, value) {
  const position = clamp((value - edgeStart) / (edgeEnd - edgeStart), 0, 1);
  return position * position * (3 - 2 * position);
}

function rootZoneVwcAtHour(hour) {
  if (hour < 6) return 540 - hour * 4;
  if (hour < 8) {
    const shotCount = Math.min(4, Math.floor((hour - 6) / 0.5) + 1);
    return 516 + shotCount * 16.5 - (hour - 6) * 3;
  }
  if (hour < 12) return 576 - (hour - 8) * 1.2;
  return 571.2 - (hour - 12) * 2.6;
}

function createSimulationHistorySample(timestamp) {
  const timeOfDay = ((timestamp % DAY_MILLISECONDS) + DAY_MILLISECONDS) % DAY_MILLISECONDS;
  const hour = timeOfDay / (60 * 60 * 1000);
  const dayFactor = smoothStep(5.75, 7, hour) - smoothStep(17.75, 19, hour);
  const dayNumber = timestamp / DAY_MILLISECONDS;
  const slowVariation = Math.sin(dayNumber * Math.PI * 2) * 1.5;
  const shortVariation = Math.sin(dayNumber * Math.PI * 24) * 0.8;

  const temperature = 242 + dayFactor * 38 + slowVariation + shortVariation;
  const humidity = 644 - dayFactor * 48 - slowVariation * 1.5 - shortVariation;
  const vpd = 52 + dayFactor * 20 + slowVariation * 0.7;
  const leafTemperature = temperature - 7 - dayFactor * 8 + Math.sin(dayNumber * 10) * 0.8;
  const co2 = 520 + dayFactor * 900 + Math.sin(dayNumber * 5) * 25;
  const baseVwc = rootZoneVwcAtHour(hour) + Math.sin(dayNumber * 8) * 1.2;

  const sample = {
    humidity: Math.round(clamp(humidity, 580, 670)),
    temp: Math.round(clamp(temperature, 230, 290)),
    co2: Math.round(clamp(co2, 450, 1500)),
    vpd: Math.round(clamp(vpd, 40, 85)),
    temp_leaf_current: Math.round(clamp(leafTemperature, 220, 280))
  };

  for (let rootZone = 0; rootZone < 4; rootZone += 1) {
    const suffix = rootZone === 0 ? '' : `_${rootZone + 1}`;
    const zoneVwc = clamp(
      baseVwc - rootZone * 4 + Math.sin(dayNumber * 6 + rootZone * 1.3) * 1.5,
      500,
      600
    );
    const zoneEc = clamp(
      242 + (550 - zoneVwc) * 0.45 + rootZone * 1.5 + Math.cos(dayNumber * 4 + rootZone),
      230,
      260
    );
    sample[`vol_water_content${suffix}`] = Math.round(zoneVwc);
    sample[`ec_root_zone${suffix}`] = Math.round(zoneEc);
  }

  return sample;
}

export function CreateSimulationHistoryBlock(history, zones, data) {
  if (zones.length > 0) {
    history['id'] = data && 'id' in data ? data.id : '0';
    history['has_zone_data'] = 1;
    return history;
  } else {
    return history;
  }
}
